import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEyeSlash,
  FaTimesCircle,
} from "react-icons/fa";
import LoadingOverlay from "../Dashboard/Common/LoadingOverlay";
import AuthShell from "./AuthShell";
import { verifyLogin } from "../utilities/authHelper";

const getPasswordChecks = (password) => ({
  length: password.length >= 10,
  upper: /[A-Z]/.test(password),
  lower: /[a-z]/.test(password),
  number: /\d/.test(password),
  special: /[^A-Za-z0-9]/.test(password),
  noControl: !/[\r\n\t]/.test(password),
});

const validatePasswordOnSubmit = (password) => {
  const errors = [];

  if (password.length < 10) {
    errors.push("Password must be at least 10 characters long.");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase character.[A-Z]");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase character.[a-z]");
  }
  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number.");
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push(
      "Password must contain at least one special character (space is allowed).",
    );
  }
  if (/[\r\n\t]/.test(password)) {
    errors.push("Password cannot contain tabs or new lines.");
  }

  return errors;
};

const ValidationChecklist = ({ items, hideSatisfied = true }) => {
  const visibleItems = hideSatisfied ? items.filter((item) => !item.ok) : items;

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className="auth-checklist auth-checklist--floating" aria-live="polite">
      <div className="auth-checklist__items">
        {visibleItems.map((item) => (
          <div
            key={item.label}
            className="auth-checklist__item is-invalid"
          >
            <FaTimesCircle />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

function Signup() {
  const location = useLocation();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [showPasswordTips, setShowPasswordTips] = useState(false);
  const [showConfirmTips, setShowConfirmTips] = useState(false);

  useEffect(() => {
    if (verifyLogin()) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const passwordErrors = validatePasswordOnSubmit(password);
    if (passwordErrors.length > 0) {
      setErrorMessage(passwordErrors);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(["Passwords do not match."]);
      return;
    }

    setErrorMessage([]);
    setIsLoading(true);

    try {
      const response = await fetch(`${apiUrl}/auth:sign_up`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          role: "USER",
          invitationCode: new URLSearchParams(location.search).get(
            "invitationCode",
          ),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorMessage([`Reason: ${data?.detail || "Signup failed."}`]);
        return;
      }

      navigate("/confirm-email");
    } catch {
      setErrorMessage(["Signup failed. Please try again."]);
    } finally {
      setIsLoading(false);
    }
  };

  const passwordChecks = getPasswordChecks(password);
  const passwordItems = [
    {
      label: "Password must be at least 10 characters long.",
      ok: passwordChecks.length,
    },
    {
      label: "Password must contain at least one uppercase character.[A-Z]",
      ok: passwordChecks.upper,
    },
    {
      label: "Password must contain at least one lowercase character.[a-z]",
      ok: passwordChecks.lower,
    },
    {
      label: "Password must contain at least one number.",
      ok: passwordChecks.number,
    },
    {
      label: "Password must contain at least one special character (space is allowed).",
      ok: passwordChecks.special,
    },
    {
      label: "Password cannot contain tabs or new lines.",
      ok: passwordChecks.noControl,
    },
  ];

  const confirmItems = [
    {
      label: "Passwords must match.",
      ok: confirmPassword.length > 0 && confirmPassword === password,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Sign Up | EMAlpha</title>
      </Helmet>

      {isLoading ? <LoadingOverlay /> : null}

      <AuthShell
        topBarPrompt="Already registered?"
        topBarCtaLabel="Log in"
        topBarCtaTo="/login"
      >
        <div>
          <p className="auth-card__eyebrow">Create account</p>
          <h2 className="auth-card__title">Sign up</h2>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-row auth-row--split">
            <div className="auth-field">
              <label htmlFor="signup-first-name">First name</label>
              <input
                id="signup-first-name"
                type="text"
                name="first_name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder="Enter your first name"
                autoComplete="given-name"
                required
              />
            </div>

            <div className="auth-field">
              <label htmlFor="signup-last-name">Last name</label>
              <input
                id="signup-last-name"
                type="text"
                name="last_name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder="Enter your last name"
                autoComplete="family-name"
                required
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="signup-email">Work email</label>
            <input
              id="signup-email"
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="auth-row">
            <div className="auth-field auth-field--with-overlay">
              <label htmlFor="signup-password">Password</label>
              <div className="auth-password-field">
                <input
                  id="signup-password"
                  type={passwordShown ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  onFocus={() => setShowPasswordTips(true)}
                  onBlur={() => setShowPasswordTips(false)}
                  placeholder="Create a secure password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setPasswordShown((shown) => !shown)}
                  aria-label={passwordShown ? "Hide password" : "Show password"}
                >
                  {passwordShown ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {showPasswordTips || password.length > 0 ? (
                <ValidationChecklist items={passwordItems} />
              ) : null}
            </div>
          </div>

          <div className="auth-row">
            <div className="auth-field auth-field--with-overlay">
              <label htmlFor="signup-confirm-password">Confirm password</label>
              <div className="auth-password-field">
                <input
                  id="signup-confirm-password"
                  type={passwordShown ? "text" : "password"}
                  name="confirm_password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  onFocus={() => setShowConfirmTips(true)}
                  onBlur={() => setShowConfirmTips(false)}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setPasswordShown((shown) => !shown)}
                  aria-label={passwordShown ? "Hide password" : "Show password"}
                >
                  {passwordShown ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {showConfirmTips || confirmPassword.length > 0 ? (
                <ValidationChecklist items={confirmItems} />
              ) : null}
            </div>
          </div>

          {errorMessage.length > 0 ? (
            <div className="auth-alert" role="alert" aria-live="polite">
              <ul>
                {errorMessage.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <p className="auth-supporting-copy auth-supporting-copy--center">
            Confirmation email required before access is activated.
          </p>

          <button type="submit" className="auth-submit">
            Create free account
          </button>

          <p className="auth-footer-copy">
            Already have an account?{" "}
            <Link to="/login" className="auth-link">
              Log in
            </Link>
          </p>
        </form>
      </AuthShell>
    </>
  );
}

export default Signup;
